import React, { useEffect, useState, useRef, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import Map from './components/Map';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import DataDeletion from './components/DataDeletion';
import Reports from './components/Reports';
import BannedUserModal from './components/BannedUserModal';
import LoginPage from './components/LoginPage';
import SignupPage from './components/SignupPage';
import ForgotPasswordPage from './components/ForgotPasswordPage';
import ResetPasswordPage from './components/ResetPasswordPage';
import VerifyEmailPage from './components/VerifyEmailPage';
import InviteFriendsModal from './components/InviteFriendsModal';
import ShareEngagementNudgeModal from './components/ShareEngagementNudgeModal';
import AccountPage from './components/AccountPage';
import AdminJurisdictionsPage from './components/AdminJurisdictionsPage';
import TennesseePlatesPage from './components/TennesseePlatesPage';
import CostcoChasePage from './components/CostcoChasePage';
import CostcoAuthCallback from './components/CostcoAuthCallback';
import { getStoredReferralCode, clearStoredReferralCode, storeReferralCode } from './lib/referralStorage';
import GameProgressDisplay from './components/GameProgressDisplay';
import {
  fetchJurisdictionsCatalog,
  JurisdictionsCatalogProvider,
} from './lib/jurisdictionsCatalog';
import './App.css';
import Cookies from 'js-cookie';

const ADMIN_EMAIL = 'joseph.e.combs@gmail.com';

function platechaseDevLog(phase, detail) {
  if (process.env.NODE_ENV !== 'development') return;
  console.info('[PlateChase dev]', phase, detail);
}

export const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? 'https://api.platechase.com'
  : 'http://localhost:8787';

// Get current month-year string for game data organization (matches server logic)
export const getCurrentMonthYear = () => {
  const now = new Date();
  // Convert to US East timezone (America/New_York)
  const eastTime = new Date(now.toLocaleString("en-US", {timeZone: "America/New_York"}));
  return eastTime.toLocaleString('default', { month: 'long' }) + '-' + eastTime.getFullYear();
};

/** Short label for the active play mode (Account → Play mode). */
export function getPlayModeLabel(playMode) {
  if (playMode === 'annual') return 'Annual';
  if (playMode === 'decennial') return 'Decennial';
  return 'Monthly';
}

function AppContent() {
  /** `pending` while validate-session runs when a session cookie exists (avoids /account hard-refresh → /login). */
  const [authStatus, setAuthStatus] = useState(() => (Cookies.get('session') ? 'pending' : 'guest'));
  const [user, setUser] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const sessionId = Cookies.get('session');
  const [visitedStates, setVisitedStates] = useState([]);
  const [gameKey, setGameKey] = useState(null);
  const [playMode, setPlayMode] = useState('monthly');
  const [removalPreference, setRemovalPreference] = useState('always_ask');
  const [stateTiers, setStateTiers] = useState({});
  const [stateSightings, setStateSightings] = useState({});
  const [windowRegionUnions, setWindowRegionUnions] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [showTooltip, setShowTooltip] = useState(false);
  const [showBannedModal, setShowBannedModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showShareNudgeModal, setShowShareNudgeModal] = useState(false);
  const [shareNudgeMilestone, setShareNudgeMilestone] = useState(null);
  const [banData, setBanData] = useState(null);
  const [mapType, setMapType] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get('map') || 'US';
  });
  const [jurisdictionsCatalog, setJurisdictionsCatalog] = useState(null);
  const [jurisdictionsCatalogLoaded, setJurisdictionsCatalogLoaded] = useState(false);

  const refreshJurisdictionsCatalog = useCallback(() => {
    return fetchJurisdictionsCatalog(API_BASE_URL, { force: true }).then((catalog) => {
      setJurisdictionsCatalog(catalog);
      setJurisdictionsCatalogLoaded(true);
      return catalog;
    });
  }, []);

  useEffect(() => {
    platechaseDevLog('app_mount', {
      host: window.location.host,
      apiBase: API_BASE_URL,
      hasSessionCookie: !!Cookies.get('session'),
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchJurisdictionsCatalog(API_BASE_URL).then((catalog) => {
      if (!cancelled) {
        setJurisdictionsCatalog(catalog);
        setJurisdictionsCatalogLoaded(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleShareEngagementNudge = useCallback((nudge) => {
    if (nudge?.show) {
      setShareNudgeMilestone(nudge.milestone ?? null);
      setShowShareNudgeModal(true);
    }
  }, []);

  const loadGameState = useCallback(() => {
    if (!sessionId) return Promise.resolve();
    const t0 = performance.now();
    return fetch(`${API_BASE_URL}/game`, {
      method: 'GET',
      headers: { Authorization: sessionId },
    })
      .then((response) => {
        if (!response.ok) {
          return response.json().then(() => {
            throw new Error(`HTTP error! status: ${response.status}`);
          });
        }
        return response.json();
      })
      .then((data) => {
        if (data.visitedStates !== undefined) {
          setVisitedStates(data.visitedStates ?? []);
        }
        if (data.gameKey !== undefined) {
          setGameKey(data.gameKey);
        }
        setPlayMode(data.playMode === 'annual' || data.playMode === 'decennial' ? data.playMode : 'monthly');
        setRemovalPreference(
          data.removalPreference === 'current_month_only' ? 'current_month_only' : 'always_ask'
        );
        setStateTiers(data.stateTiers && typeof data.stateTiers === 'object' ? data.stateTiers : {});
        setStateSightings(
          data.stateSightings && typeof data.stateSightings === 'object' ? data.stateSightings : {}
        );
        setWindowRegionUnions(
          data.windowRegionUnions && typeof data.windowRegionUnions === 'object'
            ? data.windowRegionUnions
            : null
        );
        setUser((prev) => {
          if (!prev) return prev;
          const pm = data.playMode !== undefined ? data.playMode : prev.playMode;
          const rp =
            data.removalPreference !== undefined ? data.removalPreference : prev.removalPreference;
          if (pm === prev.playMode && rp === prev.removalPreference) return prev;
          return { ...prev, playMode: pm, removalPreference: rp };
        });
      })
      .catch((error) => console.error('Error fetching game state:', error))
      .finally(() => {
        platechaseDevLog('GET /game finished', {
          ms: Math.round(performance.now() - t0),
        });
      });
  }, [sessionId]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('ref')) {
      storeReferralCode();
    }
  }, [location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const redirectPath = params.get('redirect');
    if (redirectPath) {
      navigate(redirectPath, { replace: true });
    }
  }, [location, navigate]);


  useEffect(() => {
    // Handle map parameter changes
    const params = new URLSearchParams(location.search);
    const mapParam = params.get('map');
    if (mapParam) {
      setMapType(mapParam);
    }
  }, [location]);

  useEffect(() => {
    // Log map parameter on component mount
    console.log('Map type from URL parameter:', mapType);
  }, [mapType]);

  useEffect(() => {
    if (!sessionId) {
      setAuthStatus('guest');
      setUser(null);
      console.log('Session ID is null or undefined, skipping fetch.');
      return;
    }

    setAuthStatus('pending');

    const t0 = performance.now();
    platechaseDevLog('POST /validate-session start', {
      tokenLen: sessionId.length,
    });

    fetch(`${API_BASE_URL}/validate-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'text/plain'
      },
      body: sessionId,
    })
    .then(response => {
      platechaseDevLog('POST /validate-session response', {
        ok: response.ok,
        status: response.status,
        ms: Math.round(performance.now() - t0),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(data => {
      platechaseDevLog('POST /validate-session body', { valid: data.valid });
      if (data.valid) {
        setUser(data.user);
        setAuthStatus('authenticated');
        const ref = getStoredReferralCode();
        if (ref && sessionId) {
          fetch(`${API_BASE_URL}/invites/claim`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: sessionId,
            },
            body: JSON.stringify({ ref }),
          })
            .then(() => clearStoredReferralCode())
            .catch(() => {});
        }
      } else {
        console.log('Session not valid:', data);
        setUser(null);
        setAuthStatus('guest');
      }
    })
    .catch(error => {
      console.error('Error validating session:', error);
      setUser(null);
      setAuthStatus('guest');
    });
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) {
      setVisitedStates([]);
      setGameKey(null);
      setPlayMode('monthly');
      setRemovalPreference('always_ask');
      setStateTiers({});
      setStateSightings({});
      return;
    }
    if (user) {
      loadGameState();
    }
  }, [user, sessionId, loadGameState]);

  return (
    <JurisdictionsCatalogProvider
      catalog={jurisdictionsCatalog}
      loaded={jurisdictionsCatalogLoaded}
      refreshCatalog={refreshJurisdictionsCatalog}
    >
    <div className="App">
      <nav className="App-nav">
        <div className="nav-left" ref={menuRef}>
          <button 
            className={`hamburger-menu ${isMenuOpen ? 'open' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              setIsMenuOpen(!isMenuOpen);
            }}
            aria-label="Toggle menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
          <div className={`nav-links ${isMenuOpen ? 'open' : ''}`}>
            <Link to="/" onClick={() => setIsMenuOpen(false)}>Home</Link>
            {user?.tnPlatesEnabled && (
              <Link to="/tennessee" onClick={() => setIsMenuOpen(false)}>Tennessee</Link>
            )}
            <Link to="/account" onClick={() => setIsMenuOpen(false)}>Account</Link>
            {user && (
              <button
                type="button"
                className="nav-invite-btn"
                onClick={() => {
                  setShowInviteModal(true);
                  setIsMenuOpen(false);
                }}
              >
                Invite friends
              </button>
            )}
            <Link to="/reports" onClick={() => setIsMenuOpen(false)}>Reports</Link>
            {user?.email === ADMIN_EMAIL && (
              <Link
                to="/admin/jurisdictions"
                className="nav-admin-link nav-admin-link--menu"
                onClick={() => setIsMenuOpen(false)}
              >
                Jurisdictions
              </Link>
            )}
          </div>
        </div>
        <div className="nav-center">
          <Link to="/" className="logo-link">
            <h1 className="logo-text">Plate Chase</h1>
          </Link>
        </div>
        {user ? (
          <div className="nav-right">
            {user.email === ADMIN_EMAIL && (
              <Link
                to="/admin/jurisdictions"
                className="nav-admin-link nav-admin-link--bar"
                onClick={() => setIsMenuOpen(false)}
              >
                Jurisdictions
              </Link>
            )}
            <span className="welcome-text">Welcome back, {user.name}</span>
            <Link 
              to="/" 
              onClick={(e) => {
                e.preventDefault();
                if (process.env.NODE_ENV === 'production') {
                  Cookies.remove('session', { domain: 'www.platechase.com' });
                  Cookies.remove('session', { domain: '.platechase.com' });
                } else {
                  Cookies.remove('session');
                }
                setUser(null);
                window.location.reload();
              }}
              className="logout-link"
            >
              Logout
            </Link>
          </div>
        ) : (
          <div className="nav-right nav-right--guest">
            <Link to="/login" className="nav-sign-in-btn">
              Sign in
            </Link>
          </div>
        )}
      </nav>
      <main className="App-main">
      <Routes>
        <Route path="/" element={
          <header className="App-header">
            <div className="mobile-game-info">
              <div className="progress-section progress-section--with-tooltip">
                <GameProgressDisplay
                  variant="mobile"
                  mapType={mapType}
                  playMode={playMode}
                  visitedStates={visitedStates}
                  stateTiers={stateTiers}
                  stateSightings={stateSightings}
                  currentMonthYear={getCurrentMonthYear()}
                  windowRegionUnions={windowRegionUnions}
                />
                <button
                  className="info-button info-button--mobile-progress"
                  onClick={() => setShowTooltip(!showTooltip)}
                  aria-label="Show game information"
                >
                  ⓘ
                </button>
                {showTooltip && (
                  <div className="info-tooltip">
                    Welcome to Plate Chase! Click on states as you spot their license plates on the road.
                    Progress follows your play mode (monthly, annual, or decennial) and the selected region.
                    Tap &quot;Other periods&quot; or the ▼ on desktop to see monthly vs annual vs decennial at a glance.
                    Your progress is automatically saved, and you can sync across devices by signing in.
                  </div>
                )}
              </div>
            </div>
            <Map 
              user={user} 
              visitedStates={visitedStates} 
              setVisitedStates={setVisitedStates}
              gameKey={gameKey}
              setGameKey={setGameKey}
              mapType={mapType}
              playMode={playMode}
              removalPreference={removalPreference}
              stateTiers={stateTiers}
              setStateTiers={setStateTiers}
              stateSightings={stateSightings}
              setStateSightings={setStateSightings}
              windowRegionUnions={windowRegionUnions}
              onGameDataRefresh={loadGameState}
              onShareEngagementNudge={handleShareEngagementNudge}
              onBannedUser={(banData) => {
                setBanData(banData);
                setShowBannedModal(true);
              }}
            />
          </header>
        } />
        <Route
          path="/account"
          element={
            <AccountPage
              user={user}
              setUser={setUser}
              onSettingsSaved={loadGameState}
              authStatus={authStatus}
            />
          }
        />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/data-deletion" element={<DataDeletion />} />
        <Route path="/reports" element={<Reports />} />
        <Route
          path="/costco"
          element={
            <CostcoChasePage user={user} />
          }
        />
        <Route path="/costco/auth/callback" element={<CostcoAuthCallback />} />
        <Route path="/auth/callback" element={<CostcoAuthCallback />} />
        <Route
          path="/tennessee"
          element={
            <div className="tn-plates-page">
              <TennesseePlatesPage user={user} />
            </div>
          }
        >
          <Route index element={null} />
          <Route path="plates/:plateId" element={null} />
        </Route>
        <Route path="/admin/jurisdictions" element={<AdminJurisdictionsPage user={user} />} />
      </Routes>
      </main>
      <footer className="App-footer" role="contentinfo">
        <div className="App-footer-inner">
          <p className="App-footer-brand">Plate Chase</p>
          <p className="App-footer-copy">
            © {new Date().getFullYear()} Plate Chase. All rights reserved.
          </p>
          <nav className="App-footer-nav" aria-label="Legal and policies">
            <Link to="/privacy-policy" className="App-footer-link">Privacy Policy</Link>
            <span className="App-footer-sep" aria-hidden="true">·</span>
            <Link to="/terms-of-service" className="App-footer-link">Terms of Service</Link>
            <span className="App-footer-sep" aria-hidden="true">·</span>
            <Link to="/data-deletion" className="App-footer-link">Data Deletion</Link>
          </nav>
        </div>
      </footer>
      <BannedUserModal
        isOpen={showBannedModal}
        onClose={() => setShowBannedModal(false)}
        banData={banData}
      />
      <InviteFriendsModal isOpen={showInviteModal} onClose={() => setShowInviteModal(false)} />
      <ShareEngagementNudgeModal
        isOpen={showShareNudgeModal}
        milestone={shareNudgeMilestone}
        onClose={() => setShowShareNudgeModal(false)}
        onYes={() => {
          setShowShareNudgeModal(false);
          setShowInviteModal(true);
        }}
        setUser={setUser}
      />
    </div>
    </JurisdictionsCatalogProvider>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;

